import type { ApiResponse } from 'shared';

const BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number, public details?: unknown) {
    super(message);
  }
}

export interface TokenStore {
  get: () => string | null;
  set: (t: string) => void;
  clear: () => void;
}

/**
 * A token store over localStorage. Every access is wrapped: a browser in private mode, or
 * one configured to block site data, throws on access rather than returning null.
 */
function tokenStoreFor(key: string): TokenStore {
  return {
    get: () => {
      try { return localStorage.getItem(key); } catch { return null; }
    },
    set: (t: string) => {
      try { localStorage.setItem(key, t); } catch { /* private mode */ }
    },
    clear: () => {
      try { localStorage.removeItem(key); } catch { /* private mode */ }
    },
  };
}

/**
 * The admin session and the donor microsite session live under SEPARATE keys, following
 * the microsite integration document. They are different identities with different
 * tokens, and one must never be sent to the other's endpoints — a staff member opening
 * the microsite on their own phone would otherwise clobber their backoffice session.
 */
export const tokenStore = tokenStoreFor('pmi.admin.token');
export const micrositeTokenStore = tokenStoreFor('pmi.microsite.token');

interface Client {
  get: <T>(p: string) => Promise<T>;
  post: <T>(p: string, b: unknown) => Promise<T>;
  patch: <T>(p: string, b: unknown) => Promise<T>;
  put: <T>(p: string, b: unknown) => Promise<T>;
  del: <T>(p: string) => Promise<T>;
  /** Multipart upload of a single file. */
  upload: <T>(p: string, file: File) => Promise<T>;
}

function createClient(prefix: string, tokens: TokenStore): Client {
  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = tokens.get();
    // FormData must set its OWN Content-Type: the multipart boundary is generated by the
    // browser, and naming the type here would send a header with no boundary in it.
    const isMultipart = init.body instanceof FormData;
    const res = await fetch(`${BASE}${prefix}${path}`, {
      ...init,
      headers: {
        ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });

    let body: ApiResponse<T>;
    try {
      body = await res.json();
    } catch {
      throw new ApiError('NETWORK', `Server menjawab ${res.status} tanpa isi yang bisa dibaca.`, res.status);
    }

    if (!body.success) {
      // A 401 means the session is no longer valid — clear it so it is not reused.
      if (res.status === 401) tokens.clear();
      throw new ApiError(body.error.code, body.error.message, res.status, body.error.details);
    }
    return body.data;
  }

  return {
    get: <T>(p: string) => request<T>(p),
    post: <T>(p: string, b: unknown) => request<T>(p, { method: 'POST', body: JSON.stringify(b) }),
    patch: <T>(p: string, b: unknown) => request<T>(p, { method: 'PATCH', body: JSON.stringify(b) }),
    put: <T>(p: string, b: unknown) => request<T>(p, { method: 'PUT', body: JSON.stringify(b) }),
    del: <T>(p: string) => request<T>(p, { method: 'DELETE' }),
    upload: <T>(p: string, file: File) => {
      const body = new FormData();
      body.append('file', file);
      return request<T>(p, { method: 'POST', body });
    },
  };
}

export const api = createClient('/api/v1', tokenStore);

/** Paths are relative to `/microsite`, so `get('/events')` hits the donor-facing list. */
export const micrositeApi = createClient('/api/v1/microsite', micrositeTokenStore);
